import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Lock, Download, ShieldOff } from "lucide-react";
import { api, API } from "@/lib/api";
import { StatusStamp, SealStamp } from "@/components/StatusStamp";

const DIRECTION_LABEL = { incoming: "Входящий", outgoing: "Исходящий", internal: "Внутренний" };
const ACTION_LABEL = { created: "Документ создан", sent: "Отправлен на подписание", signed: "Подписан", rejected: "Отказано в подписи" };

function fmtTime(ts) {
  return ts ? ts.slice(0, 16).replace("T", " ") : "";
}

export default function PublicDocumentView() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get(`/documents/public/${token}`)
      .then(({ data }) => setDoc(data))
      .catch(() => setError("Ссылка недействительна или документ был удалён"));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="public-view-error">
        <div className="text-center">
          <ShieldOff className="w-10 h-10 text-slate-400 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-400 text-sm" data-testid="public-view-loading">
        Загрузка документа…
      </div>
    );
  }

  const signedH = doc.history?.find((h) => h.action === "signed");
  const rejectedH = doc.history?.find((h) => h.action === "rejected");
  const signedMeta = { certificate: "01F29C8B0002AC19284D", actor: signedH?.actor_name, timestamp: fmtTime(signedH?.timestamp) };
  const rejectedMeta = { actor: rejectedH?.actor_name, reason: doc.reject_reason, timestamp: fmtTime(rejectedH?.timestamp) };
  const progressMeta = { actor: doc.created_by, timestamp: fmtTime(doc.history?.[0]?.timestamp) };

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="public-document-view">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
            <Lock className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-slate-900 font-display">КриптоДок</span>
          <span className="text-xs text-slate-400 ml-2 px-2 py-0.5 rounded-full bg-slate-100">Просмотр по ссылке, без входа в систему</span>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-5">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">
              {doc.type} №{doc.number} <span className="text-slate-400 text-sm font-normal">от {doc.doc_date}</span>
            </h1>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm bg-slate-50 rounded-lg p-4 border border-slate-200">
            <div>
              <div className="text-xs text-slate-400 uppercase">Направление</div>
              <div className="font-medium text-slate-800">{DIRECTION_LABEL[doc.direction]}</div>
            </div>
            <div>
              <div className="text-xs text-slate-400 uppercase">Контрагент</div>
              <div className="font-medium text-slate-800">{doc.counterparty_name || "—"}</div>
            </div>
            <div>
              <div className="text-xs text-slate-400 uppercase">Сумма с НДС</div>
              <div className="font-mono font-medium text-slate-800">
                {doc.amount?.toLocaleString("ru-RU")} ₽ <span className="text-slate-400">(НДС {doc.vat?.toLocaleString("ru-RU")} ₽)</span>
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-400 uppercase">Создал</div>
              <div className="font-medium text-slate-800">{doc.created_by}</div>
            </div>
          </div>

          <div>
            <StatusStamp status={doc.status} className="mb-3" />
            {doc.status === "signed" && <SealStamp status="signed" meta={signedMeta} />}
            {doc.status === "in_progress" && <SealStamp status="in_progress" meta={progressMeta} />}
            {doc.status === "rejected" && <SealStamp status="rejected" meta={rejectedMeta} />}
          </div>

          {doc.file && (
            <a
              href={`${API}/files/${doc.file.storage_path}`}
              target="_blank"
              rel="noreferrer"
              data-testid="public-doc-download-link"
              className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
            >
              <Download className="w-4 h-4" /> {doc.file.original_filename}
            </a>
          )}

          <div>
            <div className="text-xs font-semibold uppercase text-slate-400 mb-2">История</div>
            <div className="space-y-2.5">
              {doc.history?.map((h, i) => (
                <div key={i} className="flex gap-3 text-sm" data-testid={`public-doc-history-entry-${i}`}>
                  <div className="w-2 h-2 rounded-full bg-slate-300 mt-1.5 shrink-0" />
                  <div>
                    <div className="text-slate-800">
                      {ACTION_LABEL[h.action] || h.action} — <span className="font-medium">{h.actor_name}</span>
                    </div>
                    {h.comment && <div className="text-xs text-slate-500">{h.comment}</div>}
                    <div className="text-xs text-slate-400">{fmtTime(h.timestamp)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-xs text-slate-400 border-t border-slate-100 pt-3">
            Это режим просмотра для изучения документа перед подписанием. Подписать или отклонить документ можно только в личном кабинете КриптоДок.
          </p>
        </div>
      </div>
    </div>
  );
}
